import ReactGA from "react-ga4";

let initialized = false;
const STORAGE_KEY = "fired_gtag_events";
const ANALYTICS_IDS_KEY = "analytics_ids";

// Clean up session storage when the module is loaded
if (typeof window !== "undefined") {
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(ANALYTICS_IDS_KEY);
}

// Store analytics IDs when received
export const setAnalyticsIds = (analytics) => {
  if (typeof window !== "undefined" && analytics?.google) {
    sessionStorage.setItem(ANALYTICS_IDS_KEY, JSON.stringify(analytics.google));
  }
};

// Get analytics IDs
const getAnalyticsIds = () => {
  if (typeof window === "undefined") return null;
  const ids = sessionStorage.getItem(ANALYTICS_IDS_KEY);
  return ids ? JSON.parse(ids) : null;
};

export const initializeGtag = (trackingId, clientId) => {
  ReactGA.initialize(trackingId, {
    gaOptions: { clientId },
    gtagOptions: {
      cookie_flags: "SameSite=None;Secure",
      cookie_update: false
    }
  });
};

export const mapItems = (items, discountCode) =>
  items.map((item, index) => ({
    item_id: item.sku,
    item_name: item.title,
    affiliation: item.vendor || "",
    coupon: discountCode || "",
    discount: item.total_discount || item.totalDiscount,
    index,
    item_brand: item.vendor || "",
    item_category: item.product_type || item.productType,
    item_variant: item.variant_title || item.title,
    price: item.final_price || item.finalLinePrice,
    quantity: item.quantity
  }));

const getEventParams = (eventName, data) => {
  if (!eventName) return undefined;

  switch (eventName.toLowerCase()) {
    case "begin_checkout": {
      const { shopifyCart, cartDetails, applyDiscountsApi } = data;
      return {
        currency: shopifyCart?.currency,
        value: cartDetails?.finalAmount || 0,
        coupon: applyDiscountsApi?.discountCode || "",
        items: mapItems(shopifyCart.items, applyDiscountsApi?.discountCode)
      };
    }
    case "add_payment_info": {
      const { paymentMode, orderDetails } = data;
      return {
        currency: orderDetails?.currency || "INR",
        value: orderDetails?.finalAmount || 0,
        coupon: orderDetails?.cart?.discountCode || "",
        payment_type: paymentMode || "",
        items: mapItems(
          orderDetails.cart.items,
          orderDetails?.cart?.discountCode
        )
      };
    }
    case "purchase": {
      const purchaseDetails = data.orderDetails;
      return {
        currency: purchaseDetails?.currency || "INR",
        transaction_id: purchaseDetails?.paymentGatewayTxnId || "",
        value: purchaseDetails?.finalAmount || 0,
        coupon: purchaseDetails?.cart?.discountCode || "",
        shipping: purchaseDetails?.cart?.shippingPrice || 0,
        tax: purchaseDetails?.totalTax || 0,
        items: mapItems(
          purchaseDetails.cart.items,
          purchaseDetails?.cart?.discountCode
        )
      };
    }
    case "coupon_applied": {
      return { coupon: data.couponCode || "" };
    }
    case "bb_points_applied": {
      return { loyalty_points: data.loyalityPoints || 0 };
    }
    default:
      return {};
  }
};

export const fireGtagEvent = (eventName, data = {}) => {
  if (typeof window === "undefined") return undefined;
  if (typeof gtag === "undefined") return undefined;
  if (!eventName) return undefined;

  const firedEvents = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
  const eventNameLower = eventName.toLowerCase();

  if (firedEvents.includes(eventNameLower)) return undefined;

  const eventParams = getEventParams(eventName, data);
  if (!eventParams) return undefined;

  // For purchase events, send to GA4 and Google Ads separately
  if (eventNameLower === "purchase") {
    // Get the IDs from session storage
    const analyticsIds = getAnalyticsIds();
    const ga4Id = analyticsIds?.ga4_id;
    const gadsId = analyticsIds?.gads_id;

    if (ga4Id)
      window.gtag("event", "purchase", {
        ...eventParams,
        send_to: ga4Id
      });

    if (gadsId)
      window.gtag("event", "conversion", {
        ...eventParams,
        send_to: gadsId
      });
  } else ReactGA.event(eventName, eventParams);

  firedEvents.push(eventNameLower);
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(firedEvents));
  return undefined;
};

export function initializeGAds(measurementId, clientId = undefined) {
  if (initialized) return;

  const gtagScript = document.createElement("script");
  gtagScript.async = true;
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

  const configScript = document.createElement("script");
  configScript.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${measurementId}' ${clientId ? `, { client_id: '${clientId}' }` : ""});
  `;

  // Append scripts to document head
  document.head.appendChild(gtagScript);
  document.head.appendChild(configScript);

  initialized = true;
}
